import {
  doesQualifyToBeHidden,
  ItemAvailabilityIds,
} from './ItemAvailabilityStatus'
import type { TMSService } from '~/types/msService/models'
import { isKitProduct } from '~/types/product/models'
import type { IStateItem } from '~/types/models'

/**
 * # Srvs: the summed quantity of the countable rows (isCountableMSService).
 * Every service count and status / location / term breakdown in the three
 * silos is this number or a split of it, so their parts add up to it.
 */
export function getCountableMSServicesCount(
  msServices: TMSService[] | undefined | null
) {
  return sumCountableMSServicesQuantity(getCountableMSServices(msServices))
}

/** Sum of `getCountableMSServiceQuantity` over rows already known countable. */
export function sumCountableMSServicesQuantity(
  countableMSServices: TMSService[]
): number {
  return countableMSServices.reduce(
    (sum, msService) => sum + getCountableMSServiceQuantity(msService),
    0
  )
}

/**
 * What one countable row adds to # Srvs: its quantity, read from where it is
 * KEPT -- a root kit's `quantity`, a stand-alone item's `kitQuantity` (whose
 * `quantity` the server sends NULL) -- the rule `numActiveServices` follows too
 * (WXTG/cynque-api-server#1731).
 *
 * ⚠ NOT the set-up row's `quantity` for a stand-alone item. deriveMSServiceQuantity
 * writes `Number(kitQuantity) || 0` over it, so a MISSING kitQuantity reads 0 --
 * which counted a contract's only service as none and flipped it to No Services.
 *
 * ⚠ A MISSING quantity counts as 1, so a contract or service group holding a
 * service can never read 0 -- which would flip a contract to No Services and
 * re-enable Delete on a group. A real 0 stays 0.
 */
export function getCountableMSServiceQuantity(msService: TMSService): number {
  const kept: unknown = isKitProduct(msService.product)
    ? msService.quantity
    : msService.kitQuantity
  if (kept === null || kept === undefined || kept === '') {
    return 1
  }
  const value = Number(kept)
  return Number.isNaN(value) ? 1 : value
}

export function getCountableMSServices(
  msServices: TMSService[] | undefined | null
) {
  return msServices?.filter(isCountableMSService) ?? []
}

/**
 * The least an msService has to look like for `isSupersededMSService`.
 * Structural rather than `TMSService` so the menu predicates can reuse it:
 * those take a `Pick`ed slice of the entity, not the whole thing.
 */
export interface IMSServiceWithAvailability {
  itemAvailabilityState?: IStateItem
}

/**
 * Kept for the record, no longer part of the configuration — the user swapped
 * it for another alternative from the same Set, or took it out outright. This is
 * what nearly all consuming code wants: counts, money roll-ups and status
 * ladders care that the row is out, not about how it went. Which of the two it
 * was is carried by the state's own `descr` and read straight off the
 * Inclusion column; no predicate needs to tell them apart.
 *
 * 🔑 THE MARKER IS AN AVAILABILITY STATE AND NOT A STATUS, deliberately, and
 * that is worth keeping in view when reading the callers. It was tried as two
 * status ids first and the model does not survive contact with the kit
 * cascades: `setMSServiceStatus` alone is reached from 12 sites in
 * SiloOverview.ts, each passing `doIncludeDoesQualifyToBeHidden: true`, so any
 * kit-level status action wrote straight over the marker and brought the row
 * back — visible, countable and contributing money. Nothing cascades
 * itemAvailabilityState. It also lets the server agree with us through the
 * generated `qualifies_to_be_hidden` column rather than a parallel status
 * clause, and needs one shared enum instead of an id kept free in three
 * unrelated silo status spaces.
 *
 * ⚠ A superseded row therefore still carries its real lifecycle status, and
 * that status can be anything — including one a live row would have. Never
 * infer "superseded" from a status, and never treat a plausible status as
 * evidence a row is live.
 */
export function isSupersededMSService(
  msService: IMSServiceWithAvailability | undefined | null
) {
  const availabilityId = msService?.itemAvailabilityState?.id
  return (
    availabilityId === ItemAvailabilityIds.OPTIONAL_SWAPPED_OUT ||
    availabilityId === ItemAvailabilityIds.OPTIONAL_REMOVED
  )
}

/**
 * The superseded clause is now BELT-AND-BRACES rather than load-bearing, and it
 * is kept for what it says rather than what it does. `doesQualifyToBeHidden`
 * covers superseded rows by construction — both markers are availability states
 * and it tests that one field — so the clause below can no longer be the only
 * thing keeping a swapped-away row out of `numServices`, the master service's
 * status-count chips, or a contract's service count. It was exactly that while
 * the marker was a status.
 *
 * ⚠ Do not take this as licence to drop the same clause from
 * `getMSServicesToRollUp` in {Opportunity,Order,Company}MSService.ts. Those
 * never call `doesQualifyToBeHidden`, so there the test is the only exclusion
 * there is.
 *
 * The server says the same thing in its `countable_<table>` views, and — since
 * the marker moved onto `itemavailabilitystate_sn` — says it through the same
 * generated `qualifies_to_be_hidden` column this function reaches through,
 * rather than a parallel status clause that had to be kept in step by hand.
 */
/**
 * 🔑 A COUNTABLE ROW IS A ROOT ROW: a root-level kit or a stand-alone item (a
 * root-level Set's picks are stand-alone items -- a Set has no header row).
 * Items inside a kit, nested kits' included, are not counted; the kit counts
 * once, by its quantity (getCountableMSServiceQuantity). Decided 2026-11-04;
 * it was ITEM-level rows before, which counted a kit's items and never the kit.
 *
 * ⚠ A kit row has no term of its own, so a Term breakdown over this set reads
 * each root kit's from its items (getCountableMSServicesTermBreakdown).
 */
export function isCountableMSService(msService: TMSService | undefined | null) {
  return Boolean(
    msService?.doInclude &&
      !doesQualifyToBeHidden(msService) &&
      !isSupersededMSService(msService) &&
      !msService.parentMSServiceId
  )
}
